import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { MarketAutoPullPanel, type MarketAutoPullRunRow } from "./MarketAutoPullPanel";
import { MarketReportHistory } from "./MarketReportHistory";

const base = "/api/admin/market-auto-pull";
interface Status { enabled: boolean; currentMonth: string; supportsHistoricalMonths: boolean }

export function MarketAutoPullAdmin({ canManage }: { canManage: boolean }) {
  const client = useQueryClient();
  const status = useQuery<Status>({
    queryKey: [base + "/status"], enabled: canManage, refetchInterval: 15_000,
    queryFn: async () => (await apiRequest("GET", base + "/status")).json(),
  });
  const runs = useQuery<MarketAutoPullRunRow[]>({
    queryKey: [base], enabled: canManage && !status.isError && status.data?.enabled === true, refetchInterval: 5_000,
    queryFn: async () => (await apiRequest("GET", base)).json(),
  });
  if (!canManage) return null;
  if (status.isError) return <div className="space-y-3 rounded-lg border p-5"><p role="alert">Market auto-pull access could not be verified. Controls are unavailable.</p><Button variant="outline" onClick={() => void status.refetch()}>Retry availability</Button></div>;
  if (!status.data) return <div role="status" className="space-y-3 rounded-lg border p-5"><p>Checking market auto-pull availability…</p><div aria-hidden="true" className="h-24 rounded-md bg-muted motion-safe:animate-pulse" /></div>;
  const send = async (path: string, body: object) => {
    try { await apiRequest("POST", base + path, body); }
    finally { await client.invalidateQueries({ queryKey: [base] }); }
  };
  return <div className="min-w-0 space-y-6">
    <MarketReportHistory />
    {!status.data.enabled && <p className="rounded-lg border bg-muted/40 p-4 text-sm text-muted-foreground">Live collection is disconnected. Run history is unavailable until staging integration is configured. PDF import remains available below.</p>}
    {runs.isError && <div className="space-y-3 rounded-lg border border-destructive/30 p-4"><p role="alert">Run history could not be loaded. Controls are unavailable.</p><Button variant="outline" onClick={() => void runs.refetch()}>Retry run history</Button></div>}
    {status.data.enabled && runs.isPending && <div role="status" className="space-y-3 rounded-lg border p-4"><p className="text-sm text-muted-foreground">Loading recent runs…</p><div aria-hidden="true" className="h-24 rounded bg-muted motion-safe:animate-pulse" /></div>}
    <MarketAutoPullPanel
      runs={runs.data ?? []}
      currentMonth={status.data.currentMonth}
      supportsHistoricalMonths={status.data.supportsHistoricalMonths}
      enabled={status.data.enabled && runs.isSuccess && !runs.isError}
      onRetry={month => send("/retry", { month })}
      onRunNow={() => send("/run", {})}
      onResend={eventId => send("/resend-report", { eventId })}
      onCheckPublication={() => send("/check-publication", {})}
    />
  </div>;
}
